import type { BrowserBackend } from "../backend/contracts";

export type AppMigrationStep = {
  appId: string;
  fromDataSchemaVersion: number;
  toDataSchemaVersion: number;
  label: string;
};

export type AppMigrationPlan = {
  appId: string;
  fromDataSchemaVersion: number;
  toDataSchemaVersion: number;
  steps: readonly AppMigrationStep[];
};

export const TRUSTED_APP_MIGRATIONS: readonly AppMigrationStep[] = [
  {
    appId: "local-tasks",
    fromDataSchemaVersion: 1,
    toDataSchemaVersion: 2,
    label: "Add task priority with normal as the backward-compatible default.",
  },
  {
    appId: "native-workspace",
    fromDataSchemaVersion: 3,
    toDataSchemaVersion: 4,
    label: "Migrate embedded task records to the priority-aware workspace schema.",
  },
];

export function findAppMigrationPlan(
  appId: string,
  fromDataSchemaVersion: number,
  toDataSchemaVersion: number,
): AppMigrationPlan | undefined {
  if (fromDataSchemaVersion >= toDataSchemaVersion) return undefined;
  const steps: AppMigrationStep[] = [];
  let current = fromDataSchemaVersion;
  while (current < toDataSchemaVersion) {
    const step = TRUSTED_APP_MIGRATIONS.find((candidate) => candidate.appId === appId && candidate.fromDataSchemaVersion === current);
    if (!step) return undefined;
    steps.push(step);
    current = step.toDataSchemaVersion;
  }
  return { appId, fromDataSchemaVersion, toDataSchemaVersion, steps };
}

export async function applyTrustedAppMigration(
  backend: Pick<BrowserBackend, "snapshot" | "restore">,
  plan: AppMigrationPlan,
): Promise<void> {
  for (const step of plan.steps) {
    if (
      (step.appId !== "local-tasks" && step.appId !== "native-workspace") ||
      step.toDataSchemaVersion !== step.fromDataSchemaVersion + 1
    ) {
      throw new Error(`No executable migration handler is registered for ${step.appId} ${step.fromDataSchemaVersion}→${step.toDataSchemaVersion}.`);
    }
    // The current native backend's snapshot exporter/importer is the trusted data migration boundary.
    // Reading legacy records normalizes them; restoring the current snapshot materializes the new schema.
    const current = await backend.snapshot();
    await backend.restore(current);
  }
}
